import { useState, useEffect, useRef, useCallback } from "react";
import { CheckCircle, XCircle, Clock, RefreshCw, Loader2, Radio } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import API from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  submitted: "bg-[#eef1f8] text-[#0c1e41] border-[#c0cfe8]",
  queued: "bg-amber-50 text-amber-700 border-amber-200",
  processing: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  expired: "bg-slate-100 text-slate-600 border-slate-200",
};

const PENDING_STATUSES = new Set(["queued", "processing", "submitted"]);

export default function SentMessages() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.append("status", statusFilter);
      const { data } = await API.get(`/sms/messages?${params}`);
      const msgs = data.messages || [];
      setMessages(msgs);
      setTotal(data.total || 0);

      // Start polling if there are pending messages
      const hasPending = msgs.some(m => PENDING_STATUSES.has(m.status));
      if (hasPending && !pollRef.current) {
        setPolling(true);
        pollRef.current = setInterval(() => pollStatuses(msgs), 15000);
      } else if (!hasPending) {
        stopPolling();
      }
    } catch { toast.error("Failed to load messages"); }
    finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  };

  const pollStatuses = async (currentMsgs) => {
    if (!mountedRef.current) return;
    const pendingMsgs = currentMsgs.filter(m => PENDING_STATUSES.has(m.status));
    if (!pendingMsgs.length) { stopPolling(); return; }

    try {
      const updates = await Promise.allSettled(
        pendingMsgs.map(m => API.get(`/sms/messages/${m._id}/status`))
      );

      if (!mountedRef.current) return;

      let anyChanged = false;
      setMessages(prev => {
        const next = [...prev];
        updates.forEach((result, i) => {
          if (result.status === "fulfilled") {
            const { _id, status } = result.value.data;
            const idx = next.findIndex(m => m._id === _id);
            if (idx !== -1 && next[idx].status !== status) {
              next[idx] = { ...next[idx], status };
              anyChanged = true;
            }
          }
        });
        return anyChanged ? next : prev;
      });

      // Stop if no more pending
      const stillPending = pendingMsgs.filter((m, i) => {
        if (updates[i].status === "fulfilled") {
          return PENDING_STATUSES.has(updates[i].value.data.status);
        }
        return true;
      });
      if (!stillPending.length) stopPolling();
    } catch { /* silent poll error */ }
  };

  const pages = Math.ceil(total / 20);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Sent Messages</h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-sm text-slate-500">{total.toLocaleString()} total messages</p>
            {polling && (
              <span className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5 font-medium">
                <Radio size={11} className="animate-pulse" />
                Live polling
              </span>
            )}
          </div>
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={loading} className="border-slate-300 text-slate-600 h-9 hover:bg-slate-50">
          {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {["", "delivered", "submitted", "queued", "failed", "rejected"].map((s) => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); stopPolling(); }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${statusFilter === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            data-testid={`filter-status-${s || "all"}`}>
            {s || "All"}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wider">
                <th className="text-left px-5 py-3.5 font-semibold">Recipient</th>
                <th className="text-left px-5 py-3.5 font-semibold hidden sm:table-cell">Sender ID</th>
                <th className="text-left px-5 py-3.5 font-semibold">Message</th>
                <th className="text-left px-5 py-3.5 font-semibold hidden md:table-cell">Credits</th>
                <th className="text-left px-5 py-3.5 font-semibold">Status</th>
                <th className="text-left px-5 py-3.5 font-semibold hidden lg:table-cell">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? [...Array(6)].map((_, i) => (
                <tr key={i}><td colSpan={6}><div className="h-10 bg-slate-100 animate-pulse mx-5 my-2 rounded" /></td></tr>
              )) : messages.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400 text-sm">No messages found</td></tr>
              ) : messages.map((m) => (
                <tr key={m._id} className="hover:bg-slate-50 transition-colors" data-testid={`message-row-${m._id}`}>
                  <td className="px-5 py-3 font-mono text-sm font-semibold text-slate-900">{m.recipient}</td>
                  <td className="px-5 py-3 hidden sm:table-cell">
                    <span className="text-xs font-mono font-semibold text-[#0c1e41] bg-[#eef1f8] border border-[#c0cfe8] px-2 py-0.5 rounded">{m.sender_id}</span>
                  </td>
                  <td className="px-5 py-3 max-w-xs truncate text-sm text-slate-700">{m.message}</td>
                  <td className="px-5 py-3 hidden md:table-cell text-xs font-mono text-slate-600">{m.credits_used}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className={`text-xs capitalize ${STATUS_STYLES[m.status] || "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid={`status-badge-${m._id}`}>
                        {m.status}
                      </Badge>
                      {PENDING_STATUSES.has(m.status) && polling && (
                        <Loader2 size={10} className="animate-spin text-amber-500 shrink-0" />
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 hidden lg:table-cell text-xs text-slate-500">
                    {m.created_at ? new Date(m.created_at).toLocaleString("en-NG", { dateStyle: "short", timeStyle: "short" }) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200">
            <span className="text-xs text-slate-500">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 h-8 text-xs hover:bg-slate-50" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</Button>
              <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 h-8 text-xs hover:bg-slate-50" disabled={page === pages} onClick={() => setPage(page + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
